import React from 'react';
import { FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';
import { useFolderManagement } from '@/hooks/useFolderManagement';
import CollapsibleFolderStructure from '../organization/CollapsibleFolderStructure';
import { FolderItem } from '../organization/FolderStructure';
import { Folder } from '@/lib/stores/folderStore';
import { getLinkEngineItemType, getFolderItemType } from '../config/itemTypeConfig';

// Separate component for folder structure with management - prevents recreation on every render
interface FolderStructureWithManagementProps {
  folderType: FolderStructureType;
  selectedGame: any;
  selectedFolders: Map<FolderStructureType, string | null>;
  isTypeExpanded: (type: FolderStructureType) => boolean;
  toggleExpanded: (type: FolderStructureType) => void;
  setSelectedFolderForType: (type: FolderStructureType, folderId: string | null) => void;
  getItemCountForType: (type: FolderStructureType) => number;
  handleItemClick: (type: FolderStructureType) => (item: any) => void;
  handleItemRename: (type: FolderStructureType) => (itemId: string, newName: string) => Promise<void>;
  handleItemDelete: (type: FolderStructureType) => (itemId: string) => Promise<void>;
  handleItemDuplicate: (type: FolderStructureType) => (itemId: string) => Promise<void>;
  handlecreateObject: (folderId?: string) => void;
  handleCreateDocument: (folderId?: string) => void;
  handleCreateBlueprint: (folderId?: string) => void;
  handleCreateDialog: (folderId?: string) => void;
  handleCreateFlow: (folderId?: string) => Promise<void>;
  handleCreateVariable: (folderId?: string) => void;
  convertFoldersToFolderItems: (folders: Folder[], parentId?: string) => FolderItem[];
}

export const FolderStructureWithManagement = React.memo(({
  folderType,
  selectedGame,
  selectedFolders,
  isTypeExpanded,
  toggleExpanded,
  setSelectedFolderForType,
  getItemCountForType,
  handleItemClick,
  handleItemRename,
  handleItemDelete,
  handleItemDuplicate,
  handlecreateObject,
  handleCreateDocument,
  handleCreateBlueprint,
  handleCreateDialog,
  handleCreateFlow,
  handleCreateVariable,
  convertFoldersToFolderItems,
}: FolderStructureWithManagementProps) => {
  const folderManagement = useFolderManagement({ 
    itemType: getFolderItemType(folderType),
    enabled: true
  });
  
  const folderItems = convertFoldersToFolderItems(folderManagement.folders);
  const linkEngineItemType = getLinkEngineItemType(folderType);
  const selectedFolderId = selectedFolders.get(folderType) || null;

  // Get creation handler based on type
  const getCreationHandler = () => {
    switch (folderType) {
      case 'library': return handlecreateObject;
      case 'documents': return handleCreateDocument;
      case 'blueprints': return handleCreateBlueprint;
      case 'dialogs': return handleCreateDialog;
      case 'flows': return handleCreateFlow;
      case 'variables': return handleCreateVariable;
    }
  };

  return (
    <CollapsibleFolderStructure
      folderType={folderType}
      isExpanded={isTypeExpanded(folderType)}
      onToggleExpanded={() => toggleExpanded(folderType)}
      totalItemCount={getItemCountForType(folderType)}
      folders={folderItems}
      selectedFolderId={selectedFolderId}
      onFolderSelect={(folderId) => setSelectedFolderForType(folderType, folderId)}
      onFolderCreate={folderManagement.handleFolderCreate}
      onFolderRename={folderManagement.handleFolderRename}
      onFolderDelete={folderManagement.handleFolderDelete}
      onFolderColorChange={folderManagement.handleFolderColorChange}
      onItemDrop={folderManagement.handleItemDrop}
      onFolderMove={folderManagement.handleFolderMove}
      showItemCounts={false}
      allowCreation={true}
      allowRename={true}
      allowDelete={true}
      enableDragDrop={true}
      isLoading={false}
      gameId={selectedGame?.id}
      itemType={linkEngineItemType}
      showItems={true}
      onItemSelect={handleItemClick(folderType)}
      oncreateObject={folderType === 'library' ? getCreationHandler() : undefined}
      onCreateDocument={folderType === 'documents' ? getCreationHandler() : undefined}
      onCreateBlueprint={folderType === 'blueprints' ? getCreationHandler() : undefined}
      onCreateDialog={folderType === 'dialogs' ? getCreationHandler() : undefined}
      onCreateFlow={folderType === 'flows' ? getCreationHandler() : undefined}
      onCreateVariable={folderType === 'variables' ? getCreationHandler() : undefined}
      onItemRename={handleItemRename(folderType)}
      onItemDelete={handleItemDelete(folderType)}
      onItemDuplicate={handleItemDuplicate(folderType)}
      newlyCreatedFolderId={folderManagement.newlyCreatedFolderId}
      onClearNewlyCreatedFolder={folderManagement.clearNewlyCreatedFolder}
    />
  );
});

FolderStructureWithManagement.displayName = 'FolderStructureWithManagement';
